export const RISK_ASSESSMENT_SURVEY_DELAY_MS = 7_000;
export const RISK_ASSESSMENT_SURVEY_COOKIE_NAME = "seexr_survey";
export const RISK_ASSESSMENT_SURVEY_DISABLED_VALUE = "false";
export const RISK_ASSESSMENT_SURVEY_REPROMPT_DELAYS_MS = [
  30_000, 60_000, 180_000,
] as const;
export const RISK_ASSESSMENT_SURVEY_FEEDBACK_MAX_LENGTH = 1000;
export const RISK_ASSESSMENT_SURVEY_SCORE_MIN = 1;
export const RISK_ASSESSMENT_SURVEY_SCORE_MAX = 7;

export const isRiskAssessmentSurveyEnabled = (cookieValue?: string): boolean =>
  cookieValue !== RISK_ASSESSMENT_SURVEY_DISABLED_VALUE;

export type RiskAssessmentSurveyMethod =
  | "빈도강도법"
  | "3단계 판단법"
  | "체크리스트법";

export const getRiskAssessmentSurveyReprompt = (currentAttempt: number) => {
  const delayMs = RISK_ASSESSMENT_SURVEY_REPROMPT_DELAYS_MS[currentAttempt];
  return delayMs === undefined
    ? null
    : { attempt: currentAttempt + 1, delayMs };
};

export const RISK_ASSESSMENT_SURVEY_QUESTIONS = [
  {
    key: "hazardIdentification",
    label:
      "주요 유해·위험요인과 놓치기 쉬운 잠재 위험까지 충분히 식별했다.",
  },
  {
    key: "accidentClassification",
    label:
      "식별된 위험요인의 사고유형이 정확하고 적절하게 분류되었다.",
  },
  {
    key: "riskDetermination",
    label:
      "적용된 위험도 산정 기준에 따라 결정된 위험성 수준이 실제 현장 여건에 부합한다.",
  },
  {
    key: "riskReductionMeasures",
    label:
      "제시된 위험 감소대책이 구체적이고 실행 가능하며 관련 법적 기준에 근거한다.",
  },
] as const;

export const getRiskDeterminationSurveyLabel = (
  method?: RiskAssessmentSurveyMethod,
) => {
  switch (method) {
    case "빈도강도법":
      return "가능성(빈도)과 중대성(강도)을 반영해 산정된 위험성 수준이 실제 현장 여건에 부합한다.";
    case "3단계 판단법":
      return "상·중·하 3단계 판단 기준에 따라 결정된 위험성 수준이 실제 현장 여건에 부합한다.";
    case "체크리스트법":
      return "체크리스트 확인 결과와 판정 기준에 따라 결정된 위험성 수준이 실제 현장 여건에 부합한다.";
    default:
      return RISK_ASSESSMENT_SURVEY_QUESTIONS[2].label;
  }
};

export const getRiskAssessmentSurveyQuestions = (
  method?: RiskAssessmentSurveyMethod,
) =>
  RISK_ASSESSMENT_SURVEY_QUESTIONS.map((question) =>
    question.key === "riskDetermination"
      ? { ...question, label: getRiskDeterminationSurveyLabel(method) }
      : question,
  );

export type RiskAssessmentSurveyScoreKey =
  (typeof RISK_ASSESSMENT_SURVEY_QUESTIONS)[number]["key"];

export type RiskAssessmentSurveyScores = Record<
  RiskAssessmentSurveyScoreKey,
  number | null
>;

export const LIKERT_OPTIONS = [
  { value: 1, label: "전혀 그렇지 않다" },
  { value: 2, label: "그렇지 않다" },
  { value: 3, label: "약간 그렇지 않다" },
  { value: 4, label: "보통이다" },
  { value: 5, label: "약간 그렇다" },
  { value: 6, label: "그렇다" },
  { value: 7, label: "매우 그렇다" },
] as const;

export const isValidRiskAssessmentSurveyScore = (
  value: unknown,
): value is number =>
  Number.isInteger(value) &&
  Number(value) >= RISK_ASSESSMENT_SURVEY_SCORE_MIN &&
  Number(value) <= RISK_ASSESSMENT_SURVEY_SCORE_MAX;

export const emptyRiskAssessmentSurveyScores =
  (): RiskAssessmentSurveyScores => ({
    hazardIdentification: null,
    accidentClassification: null,
    riskDetermination: null,
    riskReductionMeasures: null,
  });

export const isCompleteRiskAssessmentSurvey = (
  scores: RiskAssessmentSurveyScores,
): scores is Record<RiskAssessmentSurveyScoreKey, number> =>
  RISK_ASSESSMENT_SURVEY_QUESTIONS.every(({ key }) => {
    return isValidRiskAssessmentSurveyScore(scores[key]);
  });
